import { defaultCompData, TRANSLATION_VALUE_DEFAULTS } from '../constants';
import type {
  LoginSocialBarData,
  LoggedInTextState,
} from '../LoginSocialBar.types';
import { AvatarIcon, Layout } from '../LoginSocialBar.types';

type GetSanitizedCommonCompData = (compData: LoginSocialBarData) => {
  loggedInMember: Layout;
  loggedInAvatarIcon: AvatarIcon;
  loggedOutAvatarIcon: AvatarIcon;
  loggedInTextState: LoggedInTextState;
  loggedInCustomAvatar: string;
  loggedOutCustomAvatar: string;
};

type TranslationData = {
  translationKey: string;
  value?: string;
};

// picking keys that are present in both Editor and Viewer segments
type TranslationTextParams = Pick<
  Record<keyof typeof TRANSLATION_VALUE_DEFAULTS, TranslationData>,
  'loggedInText' | 'loggedOutText' | 'logOutText' | 'customLoggedInText'
>;

export const getSanitizedCommonCompData: GetSanitizedCommonCompData =
  compData => {
    const splitAndDeprecateMemberInitials =
      compData.loggedInMember === Layout.MemberInitials;

    const loggedInMember = splitAndDeprecateMemberInitials
      ? Layout.AvatarOnly
      : (compData.loggedInMember ?? defaultCompData.loggedInMember);

    const loggedInAvatarIcon = splitAndDeprecateMemberInitials
      ? AvatarIcon.Initials
      : (compData.loggedInAvatarIcon ?? defaultCompData.loggedInAvatarIcon);

    const loggedOutAvatarIcon =
      compData.loggedOutAvatarIcon ?? loggedInAvatarIcon;

    const loggedInTextState =
      compData.loggedInTextState ?? defaultCompData.loggedInTextState;

    const loggedInCustomAvatar =
      compData.loggedInCustomAvatar ?? defaultCompData.loggedInCustomAvatar;

    const loggedOutCustomAvatar =
      compData.loggedOutCustomAvatar ?? defaultCompData.loggedOutCustomAvatar;

    return {
      loggedInMember,
      loggedInAvatarIcon,
      loggedOutAvatarIcon,
      loggedInTextState,
      loggedInCustomAvatar,
      loggedOutCustomAvatar,
    };
  };

export const getTranslatedContent = (
  params: Record<keyof TranslationTextParams, TranslationData>,
  translate: (key: string) => string | undefined,
) => {
  return Object.keys(params).reduce(
    (acc, _key) => {
      const key = _key as keyof typeof params;
      const { translationKey, value } = params[key];

      let translatedValue = value;
      const defaultValue = TRANSLATION_VALUE_DEFAULTS[key];

      // we need to check for default value because Carmi always returns truthy string
      if (!value || value === defaultValue) {
        translatedValue = translate(translationKey) || defaultValue;
      }

      return {
        ...acc,
        [key]: translatedValue,
      };
    },
    {} as Record<keyof TranslationTextParams, string>,
  );
};
